import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function authHeaders() {
  const token = localStorage.getItem('accessToken');
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function loadJson(path) {
  const response = await fetch(`${API_URL}${path}`, {
    headers: authHeaders(),
    credentials: 'include',
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.detail || `Could not load the case timeline (${response.status}).`);
  }
  return data;
}

function asDate(value, dateOnly = false) {
  if (!value) return null;
  const parsed = new Date(dateOnly ? `${value}T12:00:00` : value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateTime(value, dateOnly = false) {
  const parsed = asDate(value, dateOnly);
  if (!parsed) return '';
  return parsed.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(dateOnly ? {} : { hour: 'numeric', minute: '2-digit' }),
  });
}

function appointmentEvents(appointments) {
  return appointments.map((appointment) => ({
    id: `appointment-${appointment.id}`,
    occurredAt: appointment.starts_at,
    dateOnly: false,
    category: 'appointment',
    title: appointment.appointment_type_display || 'Client appointment',
    status: appointment.status,
    details: appointment.service_name || '',
    location: appointment.location || '',
    contact: [appointment.customer_phone, appointment.customer_email].filter(Boolean).join(' · '),
    notes: appointment.internal_notes || '',
    history: Array.isArray(appointment.history) ? appointment.history : [],
  }));
}

function caseDateEvents(caseInfo, appointments) {
  if (!caseInfo) return [];
  const events = [];
  const add = (id, occurredAt, title, category, dateOnly = false, details = '') => {
    if (occurredAt) events.push({ id, occurredAt, title, category, dateOnly, details });
  };

  add('case-created', caseInfo.created_at, 'Case created', 'case');
  add('case-filed', caseInfo.date_filed, 'Case filed', 'case', true, caseInfo.case_number || '');
  add('meeting-341', caseInfo.meeting_date, 'Meeting of Creditors (341)', 'court');
  add('confirmation', caseInfo.confirmation_date, 'Confirmation hearing', 'court');
  add('reaffirmation-deadline', caseInfo.reaffirmation_deadline, 'Reaffirmation deadline', 'deadline', true);
  add('claims-deadline', caseInfo.proof_of_claims_deadline, 'Proof of claims deadline', 'deadline', true);
  add('financial-management-deadline', caseInfo.financial_management_deadline, 'Financial management deadline', 'deadline', true);
  add('discharge', caseInfo.discharge_date, 'Discharge', 'case', true);

  const signingTime = asDate(caseInfo.case_signing_date);
  const hasImportedSigning = appointments.some((appointment) => {
    if (appointment.appointment_type !== 'case_filing' || appointment.status !== 'scheduled') return false;
    const importedTime = asDate(appointment.starts_at);
    return signingTime && importedTime && Math.abs(signingTime - importedTime) < 60000;
  });
  if (!hasImportedSigning) {
    add('legacy-signing', caseInfo.case_signing_date, 'Case Filing', 'appointment');
  }
  return events;
}

function hearingEvents(hearings) {
  return hearings.map((hearing) => ({
    id: `hearing-${hearing.id}`,
    occurredAt: hearing.starts_at,
    dateOnly: false,
    category: 'court',
    title: hearing.caption || 'Court hearing',
    status: hearing.status,
    details: [hearing.hearing_type, hearing.courtroom].filter(Boolean).join(' · '),
    location: [hearing.location_name, hearing.location_city, hearing.location_state]
      .filter(Boolean)
      .join(', '),
  }));
}

const CATEGORY_STYLES = {
  appointment: 'border-blue-200 bg-blue-50 text-blue-800',
  court: 'border-purple-200 bg-purple-50 text-purple-800',
  deadline: 'border-amber-200 bg-amber-50 text-amber-800',
  case: 'border-emerald-200 bg-emerald-50 text-emerald-800',
};

function TimelineCard({ event }) {
  const appointmentHistory = Array.isArray(event.history) ? event.history : [];
  const rescheduleHistory = event.category === 'appointment'
    ? appointmentHistory.filter((item) => item?.starts_at)
    : [];
  const previousTimes = rescheduleHistory
    .slice(0, -1)
    .map((item) => item.starts_at)
    .filter((value) => {
      const prior = asDate(value);
      const current = asDate(event.occurredAt);
      return prior && current && prior.getTime() !== current.getTime();
    })
    .filter((value, index, values) => values.indexOf(value) === index);

  return (
    <article className={`rounded-xl border p-4 ${CATEGORY_STYLES[event.category] || CATEGORY_STYLES.case} ${event.status === 'canceled' ? 'opacity-65' : ''}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className={`font-semibold ${event.status === 'canceled' ? 'line-through' : ''}`}>
              {event.title}
            </h3>
            {event.status && (
              <span className="rounded-full border border-current/20 bg-white/70 px-2 py-0.5 text-xs font-medium capitalize">
                {event.status}
              </span>
            )}
            {previousTimes.length > 0 && (
              <span className="rounded-full border border-blue-300 bg-white/70 px-2 py-0.5 text-xs font-medium">
                Rescheduled
              </span>
            )}
          </div>
          {event.details && <p className="mt-1 text-sm opacity-80">{event.details}</p>}
        </div>
        <time className="shrink-0 text-sm font-medium">
          {formatDateTime(event.occurredAt, event.dateOnly)}
        </time>
      </div>
      {event.location && <p className="mt-3 text-sm"><span className="font-medium">Location:</span> {event.location}</p>}
      {event.contact && <p className="mt-1 text-sm"><span className="font-medium">Client:</span> {event.contact}</p>}
      {event.notes && <p className="mt-1 whitespace-pre-wrap text-sm"><span className="font-medium">Notes:</span> {event.notes}</p>}
      {previousTimes.length > 0 && (
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer font-medium">Previous appointment time{previousTimes.length === 1 ? '' : 's'}</summary>
          <ul className="mt-1 list-disc space-y-1 pl-5">
            {previousTimes.map((value) => <li key={value}>{formatDateTime(value)}</li>)}
          </ul>
        </details>
      )}
    </article>
  );
}

export default function TimelinePage() {
  const { caseId } = useParams();
  const [caseInfo, setCaseInfo] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [hearings, setHearings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      loadJson(`/case/${caseId}/overview/`),
      loadJson(`/case/${caseId}/appointments/`),
      loadJson(`/case/${caseId}/hearings/`),
    ])
      .then(([overview, appointmentData, hearingData]) => {
        if (!active) return;
        setCaseInfo(overview);
        setAppointments(Array.isArray(appointmentData) ? appointmentData : []);
        setHearings(Array.isArray(hearingData) ? hearingData : []);
        setError('');
      })
      .catch((err) => {
        if (active) setError(err.message || 'Could not load the case timeline.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [caseId]);

  const events = useMemo(() => {
    const combined = [
      ...appointmentEvents(appointments),
      ...caseDateEvents(caseInfo, appointments),
      ...hearingEvents(hearings),
    ].filter((event) => asDate(event.occurredAt, event.dateOnly));
    return combined.sort((a, b) => (
      asDate(a.occurredAt, a.dateOnly) - asDate(b.occurredAt, b.dateOnly)
    ));
  }, [appointments, caseInfo, hearings]);

  const filteredEvents = filter === 'all'
    ? events
    : events.filter((event) => event.category === filter);
  const now = new Date();
  const upcoming = filteredEvents.filter((event) => asDate(event.occurredAt, event.dateOnly) >= now);
  const history = filteredEvents
    .filter((event) => asDate(event.occurredAt, event.dateOnly) < now)
    .reverse();

  if (loading) return <div className="rounded-lg bg-white p-6 shadow">Loading case timeline…</div>;
  if (error) return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Case Timeline</h1>
          <p className="mt-1 text-sm text-gray-600">
            Client appointments, court dates, filing milestones, and deadlines in one place.
          </p>
        </div>
        <label className="text-sm font-medium text-gray-700">
          Show
          <select
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            className="ml-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm"
          >
            <option value="all">Everything</option>
            <option value="appointment">Client appointments</option>
            <option value="court">Court dates</option>
            <option value="deadline">Deadlines</option>
            <option value="case">Case milestones</option>
          </select>
        </label>
      </div>

      <section>
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Upcoming</h2>
        <div className="space-y-3">
          {upcoming.length
            ? upcoming.map((event) => <TimelineCard key={event.id} event={event} />)
            : <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-500">No upcoming items in this view.</div>}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-lg font-semibold text-gray-900">History</h2>
        <div className="space-y-3">
          {history.length
            ? history.map((event) => <TimelineCard key={event.id} event={event} />)
            : <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-500">No past items in this view.</div>}
        </div>
      </section>
    </div>
  );
}
